// src/components/OAuthCallback.jsx - OAuth callback handler
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api';

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Processing...');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Get the authorization code and provider from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');

        if (errorParam) {
          setError(`Authentication failed: ${errorParam}`);
          setStatus('Error');
          // Notify parent window
          if (window.opener) {
            window.opener.postMessage({
              type: 'social-auth-error',
              error: errorParam
            }, window.location.origin);
            setTimeout(() => window.close(), 2000);
          }
          return;
        }

        if (!code) {
          setError('No authorization code received');
          setStatus('Error');
          return;
        }

        // Determine provider from state or path
        const provider = state || 'google'; // Default to google for now

        setStatus('Exchanging authorization code...');

        // Exchange code for token and authenticate
        const response = await apiService.post('/auth/social/exchange', {
          code: code,
          provider: provider
        });

        if (response && response.access_token && response.user) {
          setStatus('Authentication successful! Redirecting...');

          // Store tokens in localStorage
          localStorage.setItem('token', response.access_token);
          if (response.refresh_token) {
            localStorage.setItem('refresh_token', response.refresh_token);
          }

          // Redirect to dashboard
          setTimeout(() => {
            // Force a full page reload to update auth state
            window.location.href = '/';
          }, 1000);
        } else {
          console.error('Invalid response:', response);
          throw new Error('Invalid response from server - missing access_token or user');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Authentication failed';
        console.error('Error details:', errorMessage);
        setError(errorMessage);
        setStatus('Error');

        // Show error for 5 seconds then redirect to login
        setTimeout(() => {
          window.location.href = '/';
        }, 5000);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
        {error ? (
          <>
            <div className="text-red-500 text-6xl mb-4">✗</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Authentication Failed
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
            {!window.opener && (
              <button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-6 py-2 rounded-lg transition-all font-medium"
              >
                Return to Login
              </button>
            )}
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pink-500 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              {status}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we complete your sign in...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
